import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { PannellumViewer } from '@ironview/interactive-viewer';

import type { SkyMapNode } from './FlightPathPanel';
import {
  buildGroundPanoramaMosaic,
  projectGroundMosaicCell,
  projectGroundPolygon,
  type SkyMapPanoramaNavigation,
} from './skyMapPanoramaNavigation';

interface Props {
  viewerRef: MutableRefObject<PannellumViewer | null>;
  nodes: readonly SkyMapNode[];
  currentNode: SkyMapNode;
  navigation: SkyMapPanoramaNavigation;
  onNavigate: (node: SkyMapNode) => void;
}

const DRAG_THRESHOLD_PX = 6;

export const buildOutsideBlurMaskImage = (
  perimeterPath: string | null,
  width: number,
  height: number,
) => {
  if (!perimeterPath || width <= 0 || height <= 0) return null;
  const maskPath = `M0 0H${width}V${height}H0Z${perimeterPath}`;
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">`,
    `<path fill="white" fill-rule="evenodd" d="${maskPath}"/>`,
    '</svg>',
  ].join('');
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

export const SkyMapPanoramaMosaic = ({
  viewerRef,
  nodes,
  currentNode,
  navigation,
  onNavigate,
}: Props) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const perimeterPathRef = useRef<SVGPathElement>(null);
  const outsideBlurRef = useRef<HTMLDivElement>(null);
  const pathRefs = useRef(new Map<string, SVGPathElement>());
  const gestureRef = useRef<{ x: number; y: number; dragged: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const suppressionTimerRef = useRef<number>();
  const mosaic = useMemo(
    () => buildGroundPanoramaMosaic(nodes, navigation),
    [navigation, nodes],
  );

  const setPathRef = useCallback((nodeId: string, element: SVGPathElement | null) => {
    if (element) pathRefs.current.set(nodeId, element);
    else pathRefs.current.delete(nodeId);
  }, []);

  useEffect(() => {
    let animationFrame = 0;
    let previousView = '';
    const update = () => {
      const svg = svgRef.current;
      const viewer = viewerRef.current;
      if (svg && viewer && mosaic) {
        const bounds = svg.getBoundingClientRect();
        const viewport = {
          width: bounds.width,
          height: bounds.height,
          pitch: viewer.getPitch(),
          yaw: viewer.getYaw(),
          hfov: viewer.getHfov(),
        };
        const viewKey = Object.values(viewport).map((value) => value.toFixed(3)).join(':');
        if (viewport.width > 0 && viewport.height > 0 && viewKey !== previousView) {
          previousView = viewKey;
          svg.setAttribute('viewBox', `0 0 ${viewport.width} ${viewport.height}`);
          mosaic.cells.forEach((cell) => {
            const path = pathRefs.current.get(String(cell.node.id));
            if (!path) return;
            const pathData = projectGroundMosaicCell(cell, currentNode, viewport);
            if (pathData) {
              path.setAttribute('d', pathData);
              path.style.display = '';
            } else {
              path.removeAttribute('d');
              path.style.display = 'none';
            }
          });
          const perimeterPathData = projectGroundPolygon(
            mosaic.worldPerimeter,
            currentNode,
            viewport,
          );
          if (perimeterPathRef.current) {
            if (perimeterPathData) {
              perimeterPathRef.current.setAttribute('d', perimeterPathData);
              perimeterPathRef.current.style.display = '';
            } else {
              perimeterPathRef.current.removeAttribute('d');
              perimeterPathRef.current.style.display = 'none';
            }
          }
          if (outsideBlurRef.current) {
            const maskImage = buildOutsideBlurMaskImage(
              perimeterPathData,
              viewport.width,
              viewport.height,
            );
            if (maskImage) {
              outsideBlurRef.current.style.maskImage = maskImage;
              outsideBlurRef.current.style.setProperty('-webkit-mask-image', maskImage);
              outsideBlurRef.current.style.visibility = 'visible';
            } else {
              outsideBlurRef.current.style.maskImage = '';
              outsideBlurRef.current.style.removeProperty('-webkit-mask-image');
              outsideBlurRef.current.style.visibility = 'hidden';
            }
          }
        }
      }
      animationFrame = requestAnimationFrame(update);
    };
    animationFrame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animationFrame);
  }, [currentNode, mosaic, viewerRef]);

  useEffect(() => {
    const markMovement = (event: MouseEvent) => {
      const gesture = gestureRef.current;
      if (!gesture) return;
      if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y)
        > DRAG_THRESHOLD_PX) {
        gesture.dragged = true;
      }
    };
    const finishGesture = () => {
      const gesture = gestureRef.current;
      if (!gesture) return;
      suppressClickRef.current = gesture.dragged;
      gestureRef.current = null;
      if (suppressionTimerRef.current !== undefined) {
        window.clearTimeout(suppressionTimerRef.current);
      }
      suppressionTimerRef.current = window.setTimeout(() => {
        suppressClickRef.current = false;
        suppressionTimerRef.current = undefined;
      }, 0);
    };
    document.addEventListener('mousemove', markMovement);
    document.addEventListener('mouseup', finishGesture);
    return () => {
      document.removeEventListener('mousemove', markMovement);
      document.removeEventListener('mouseup', finishGesture);
      if (suppressionTimerRef.current !== undefined) {
        window.clearTimeout(suppressionTimerRef.current);
      }
    };
  }, []);

  const dragSurface = () => svgRef.current?.parentElement
    ?.querySelector<HTMLElement>('.pnlm-dragfix');

  const handleMouseDown = (event: ReactMouseEvent<SVGPathElement>) => {
    if (event.button !== 0) return;
    const surface = dragSurface();
    if (!surface) return;
    suppressClickRef.current = false;
    gestureRef.current = { x: event.clientX, y: event.clientY, dragged: false };
    surface.dispatchEvent(new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
      clientX: event.clientX,
      clientY: event.clientY,
      button: event.button,
      buttons: event.buttons,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
    }));
  };

  const forwardTouchPointer = (
    type: 'pointerdown' | 'pointermove' | 'pointerup' | 'pointercancel',
    event: ReactPointerEvent<SVGPathElement>,
  ) => {
    if (event.pointerType === 'mouse') return;
    const surface = dragSurface();
    const PointerEventConstructor = window.PointerEvent;
    if (!surface || !PointerEventConstructor) return;
    surface.dispatchEvent(new PointerEventConstructor(type, {
      bubbles: true,
      cancelable: true,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      isPrimary: event.isPrimary,
      clientX: event.clientX,
      clientY: event.clientY,
      button: event.button,
      buttons: event.buttons,
      pressure: event.pressure,
    }));
  };

  const handlePointerDown = (event: ReactPointerEvent<SVGPathElement>) => {
    if (event.pointerType === 'mouse') return;
    event.preventDefault();
    suppressClickRef.current = false;
    gestureRef.current = { x: event.clientX, y: event.clientY, dragged: false };
    event.currentTarget.setPointerCapture(event.pointerId);
    forwardTouchPointer('pointerdown', event);
  };
  const handlePointerMove = (event: ReactPointerEvent<SVGPathElement>) => {
    if (event.pointerType === 'mouse' || !gestureRef.current) return;
    const gesture = gestureRef.current;
    if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y)
      > DRAG_THRESHOLD_PX) {
      gesture.dragged = true;
    }
    forwardTouchPointer('pointermove', event);
  };
  const handlePointerEnd = (event: ReactPointerEvent<SVGPathElement>) => {
    if (event.pointerType === 'mouse' || !gestureRef.current) return;
    forwardTouchPointer(event.type as 'pointerup' | 'pointercancel', event);
    suppressClickRef.current = gestureRef.current.dragged;
    gestureRef.current = null;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  if (!mosaic) return null;

  return (
    <>
      <div
        ref={outsideBlurRef}
        className="sky-map-panorama-mosaic__outside-blur"
        aria-hidden="true"
      />
      <svg
        ref={svgRef}
        className="sky-map-panorama-mosaic"
        aria-label="Panorama coverage areas"
      >
        {mosaic.cells.map((cell) => {
          const isCurrent = cell.node.id === currentNode.id;
          return (
            <path
              key={cell.node.id}
              ref={(element) => setPathRef(String(cell.node.id), element)}
              className={`sky-map-panorama-mosaic__cell${isCurrent ? ' is-current' : ''}`}
              role={isCurrent ? undefined : 'button'}
              focusable="false"
              aria-label={isCurrent
                ? `Current panorama, Node ${cell.node.frame}`
                : `Move to Node ${cell.node.frame}`}
              aria-current={isCurrent ? 'location' : undefined}
              onMouseDown={isCurrent ? undefined : handleMouseDown}
              onPointerDown={isCurrent ? undefined : handlePointerDown}
              onPointerMove={isCurrent ? undefined : handlePointerMove}
              onPointerUp={isCurrent ? undefined : handlePointerEnd}
              onPointerCancel={isCurrent ? undefined : handlePointerEnd}
              onClick={isCurrent ? undefined : (event) => {
                if (suppressClickRef.current) {
                  event.preventDefault();
                  event.stopPropagation();
                  suppressClickRef.current = false;
                  return;
                }
                onNavigate(cell.node);
              }}
            />
          );
        })}
        <path
          ref={perimeterPathRef}
          className="sky-map-panorama-mosaic__perimeter"
          aria-hidden="true"
        />
      </svg>
    </>
  );
};
